/**
 * SSO logout teardown.
 *
 * The app's "logged in" state is the redux `auth` slice, which is PERSISTED by redux-persist
 * (key `persist:v100-demo1-auth`, whitelisting `token` + `user`). It rehydrates on every page
 * load. So `dispatch(logout())` alone does NOT log the user out: `signoutRedirect()` navigates
 * to Keycloak before redux-persist's async write flushes the cleared state, so the OLD
 * `token`+`user` are still in localStorage and get rehydrated on return → the app looks logged in.
 *
 * Fix: SYNCHRONOUSLY remove the persisted auth blob (and the bridged token + oidc-client-ts's
 * stored user/state) BEFORE the full-page navigation, so nothing rehydrates or re-bridges. We do
 * NOT call oidc.removeUser() here — that flips isAuthenticated mid-page, re-runs AuthInit, and
 * races a second redirect. AuthInit forces a fresh KC login (prompt=login) on return via the
 * `sso_logout` marker.
 */

// redux-persist key for the auth slice (see AuthRedux.ts persistReducer config).
const PERSIST_AUTH_KEY = 'persist:v100-demo1-auth'

interface OidcLike {
  user?: {id_token?: string} | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  signoutRedirect: (args?: any) => Promise<unknown>
}

export function clearSsoSession(oidc: OidcLike): void {
  const idToken = oidc.user?.id_token
  try {
    localStorage.removeItem('token')
    localStorage.removeItem(PERSIST_AUTH_KEY)
    // oidc-client-ts stores the user + signin state under `oidc.*` keys — clear them so a stale
    // user can't report isAuthenticated=true and re-bridge on the next load.
    Object.keys(localStorage)
      .filter((k) => k.startsWith('oidc.'))
      .forEach((k) => localStorage.removeItem(k))
  } catch {
    /* ignore storage access errors */
  }
  // Force a fresh KC login (prompt=login) when we return unauthenticated.
  sessionStorage.setItem('sso_logout', '1')
  // Exactly ONE navigation: end the KC session (id_token as hint for a clean end-session).
  oidc.signoutRedirect(idToken ? {id_token_hint: idToken} : undefined).catch(() => {
    window.location.href = '/'
  })
}
